import React, { useMemo } from 'react';
import styled, { withTheme } from 'styled-components';
import Highchart from '../../../highchart/Highchart';
import type { growthSimulationConfigProps } from '../../../charts/analysis-charts/chart-config-logic';
import {
  createGrowthSimulationLineChartConfig,
  getLineSerie,
} from '../../../charts/analysis-charts/chart-config-logic';
import { getCurrencySymbol } from './chartsUtils';
import { compact } from 'lodash';
import type { GrowthSimulationChartProps, SubjectInfo } from './types';
import { REPORT_LAB_FONT_BODY, Icon } from 'venn-ui-kit';
import { assertNotNil, Numbers } from 'venn-utils';
import { NameWithOptionalFee } from '../../../legend';
import { LayoutVersion } from 'venn-state';
import { useStudioLayoutVersion } from '../../../hooks';

const GrowthSimulationLineChart = ({
  start,
  end,
  theme,
  series,
  frequency,
  inPrintMode,
  hasDrawdownLines,
  height,
  useLogarithmicScale,
  currency,
  subjectInfo,
}: GrowthSimulationChartProps) => {
  const currencySymbol = currency ? getCurrencySymbol(currency) : '';
  const layoutVersion = useStudioLayoutVersion();

  const seriesValues = useMemo(
    () =>
      compact(
        series.map((serie) => {
          const color = assertNotNil(serie.color, 'Every serie should be assigned a color');
          const lineSerie = getLineSerie(serie.data, serie.name, color);
          return lineSerie
            ? {
                data: lineSerie,
                name: serie.name,
                legendLabel: serie.name,
              }
            : undefined;
        }),
      ),
    [series],
  );

  const options = useMemo(() => {
    const config = {
      ...createGrowthSimulationLineChartConfig({
        start,
        end,
        series: seriesValues,
        theme,
        frequency,
        print: inPrintMode,
        hasDrawdownLines,
        currencySymbol,
        useLogarithmicScale: useLogarithmicScale ?? true,
      } as growthSimulationConfigProps),
    };

    return {
      ...config,
      chart: {
        ...config.chart,
        height,
      },
      legend: {
        enabled: false,
      },
      plotOptions: {
        ...config.plotOptions,
      },
    };
  }, [
    start,
    end,
    theme,
    frequency,
    inPrintMode,
    hasDrawdownLines,
    useLogarithmicScale,
    currencySymbol,
    seriesValues,
    height,
  ]);

  const getCustomLegend = (subjectInfo: SubjectInfo[]) => (
    <div>
      {subjectInfo.map((info, idx) => {
        const valuesForSubject =
          subjectInfo.length > 1
            ? idx === 0
              ? seriesValues.slice(0, seriesValues.length / 2)
              : seriesValues.slice(seriesValues.length / 2)
            : seriesValues;
        return (
          <>
            <RowWrapper layoutVersion={layoutVersion}>
              <NameWithOptionalFee subject={subjectInfo[idx].subject} />
              {valuesForSubject.map((value) => {
                return (
                  <IconWrapper>
                    <Icon style={{ color: value?.data.color }} type="circle" />
                    &nbsp;{value.name}
                  </IconWrapper>
                );
              })}
              <b>Return (forecast):</b>&nbsp;{Numbers.safeFormatPercentage(info.return)}&nbsp;
              <b>Volatility (forecast):</b>&nbsp;{Numbers.safeFormatPercentage(info.volitility)}
            </RowWrapper>
          </>
        );
      })}
    </div>
  );
  return (
    <>
      <Highchart options={options} />
      <LegendWrapper className="qa-growth-simulation-legend">
        {subjectInfo && getCustomLegend(subjectInfo)}
      </LegendWrapper>
    </>
  );
};

export default withTheme(GrowthSimulationLineChart);

const RowWrapper = styled.div<{ layoutVersion: LayoutVersion }>`
  color: #333333;
  ${({ layoutVersion }) =>
    layoutVersion === LayoutVersion.MODERN ? `font-size: ${REPORT_LAB_FONT_BODY}` : 'font-size: 12px'};
  font-weight: bold;
  display: flex;
  flex-wrap: wrap;
`;

const LegendWrapper = styled.div`
  justify-content: center;
  align-items: center;
  display: flex;
  flex-wrap: wrap;
`;

const IconWrapper = styled.div`
  margin: 0 5px;
`;
